import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faEye, faStar, faLightbulb, faCog, faPenNib } from '@fortawesome/free-solid-svg-icons';

import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../@style/AboutPage.css';

const AboutPage = () => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  useEffect(() => {
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      // Make hero elements visible immediately
      const heroElements = document.querySelectorAll('.about-hero .fade-in-up');
      heroElements.forEach(element => {
        element.classList.add('initial');
      });

      const observerOptions = {
        threshold: 0.05,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          } else {
            // Element viewport'tan çıktığında animasyon sınıfını kaldır
            entry.target.classList.remove('animate');
          }
        });
      }, observerOptions);

      // Observe all animated elements except hero elements
      const animatedElements = document.querySelectorAll('.fade-in-up:not(.about-hero .fade-in-up), .fade-in-left, .fade-in-right, .scale-in');
      
      animatedElements.forEach(element => {
        observer.observe(element);
      });

      return () => {
        animatedElements.forEach(element => {
          observer.unobserve(element);
        });
      };
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`about-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <h1 className="fade-in-up">{t('about.title')}</h1>
          <p className="fade-in-up">{t('about.subtitle')}</p>
        </div>
      </section>

      {/* History Section */}
      <section className="history-section">
        <div className="container">
          <h2 className="fade-in-up">{t('about.history.title')}</h2>
          <div className="history-content fade-in-up">
            <p>
              {t('about.history.content1')}
            </p>
            <p>
              {t('about.history.content2')}
            </p>
            <p>
              {t('about.history.content3')}
            </p>
            <p>
              {t('about.history.content4')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values Section */}
      <section className="mission-vision-values">
        <div className="container">
          <div className="values-grid">
            <div className="value-card fade-in-up">
              <div className="value-icon mission">
                <FontAwesomeIcon icon={faRocket} />
              </div>
              <h3>{t('about.mission.title')}</h3>
              <p>
                {t('about.mission.content')}
              </p>
            </div>

            <div className="value-card fade-in-up">
              <div className="value-icon vision">
                <FontAwesomeIcon icon={faEye} />
              </div>
              <h3>{t('about.vision.title')}</h3>
              <p>
                {t('about.vision.content')}
              </p>
            </div>

            <div className="value-card fade-in-up">
              <div className="value-icon values">
                <FontAwesomeIcon icon={faStar} />
              </div>
              <h3>{t('about.values.title')}</h3>
              <p>
                {t('about.values.content')}
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* Trust Process Section */}
      <section className="trust-process">
        <div className="container">
          <h2 className="fade-in-up">{t('about.trust.title')}</h2>
          <p className="trust-description fade-in-up">
            {t('about.trust.description')}
          </p>
          
          <div className="process-timeline">
            <div className="timeline-line"></div>
            
            <div className="process-step left">
              <div className="step-number scale-in">01</div>
              <div className="step-content fade-in-left">
                <h3>{t('about.trust.step1.title')}</h3>
                <p>
                  {t('about.trust.step1.content')}
                </p>
              </div>
              <div className="step-icon scale-in">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
            </div>

            <div className="process-step right">
              <div className="step-number scale-in">02</div>
              <div className="step-content fade-in-right">
                <h3>{t('about.trust.step2.title')}</h3>
                <p>
                  {t('about.trust.step2.content')}
                </p>
              </div>
              <div className="step-icon scale-in">
                <FontAwesomeIcon icon={faCog} />
              </div>
            </div>

            <div className="process-step left">
              <div className="step-number scale-in">03</div>
              <div className="step-content fade-in-left">
                <h3>{t('about.trust.step3.title')}</h3>
                <p>
                  {t('about.trust.step3.content')}
                </p>
              </div>
              <div className="step-icon scale-in">
                <FontAwesomeIcon icon={faPenNib} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="mobile-app-section">
        <div className="container">
          <div className="app-content">
            <div className="app-text">
              <h2 className="fade-in-up">{t('about.mobile.title')}</h2>
              <div className="app-features">
                <div className="app-feature fade-in-up">
                  <h4>{t('about.mobile.feature1.title')}</h4>
                  <p>{t('about.mobile.feature1.content')}</p>
                </div>
                <div className="app-feature fade-in-up">
                  <h4>{t('about.mobile.feature2.title')}</h4>
                  <p>{t('about.mobile.feature2.content')}</p>
                </div>
                <div className="app-feature fade-in-up">
                  <h4>{t('about.mobile.feature3.title')}</h4>
                  <p>{t('about.mobile.feature3.content')}</p>
                </div>
                <div className="app-feature fade-in-up">
                  <h4>{t('about.mobile.feature4.title')}</h4>
                  <p>{t('about.mobile.feature4.content')}</p>
                </div>
                <div className="app-feature fade-in-up">
                  <h4>{t('about.mobile.feature5.title')}</h4>
                  <p>{t('about.mobile.feature5.content')}</p>
                </div>
              </div>
              <div className="app-download fade-in-up">
                <h3>{t('about.mobile.download.title')}</h3>
                <div className="download-buttons">
                  <a 
                    href="https://apps.apple.com/sn/app/masgold/id6753982731" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download-btn app-store"
                  >
                    <img src={require('../assets/icons/appstore.png')} alt={t('common.app_store')} />
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.aifasoft.masgold&hl=tr" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download-btn google-play"
                  >
                    <img src={require('../assets/icons/gplay.png')} alt={t('common.google_play')} />
                  </a>
                </div>
              </div>
            </div>
            <div className="app-mockups">
              <div className="phone-mockup light-theme fade-in-right">
                <img 
                  src={require('../assets/images/mob.jpg')} 
                  alt={t('about.mobile.light_theme')} 
                  className="mockup-image"
                />
              </div>
              <div className="phone-mockup dark-theme fade-in-right">
                <img 
                  src={require('../assets/images/mob2.jpg')} 
                  alt={t('about.mobile.dark_theme')} 
                  className="mockup-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
